import React, { FC, useState } from "react";
import CommonLayout from "./CommonLayout";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useLoader } from "../../@shared/hooks/useLoader";
import { useAlert } from "../../@shared/hooks/useAlert";
import { useFAQ } from "../../hooks/collections/useFAQ";
import { IFAQ } from "../../@core/interfaces/IFAQ";
import useModal from "../../@shared/components/Modal/hooks/useModal";
import { orderBy } from "lodash";
import { FAQForm } from "../AccountPage/FAQForm/FAQForm";
import { FAQItem } from "../AccountPage/FAQForm/FAQItem";

export interface AccountFAQProps {
  className?: string;
}

const AccountFAQ: FC<AccountFAQProps> = ({ className = "" }) => {
  const loader = useLoader();
  const alert = useAlert();
  const { faqs, createFAQ, updateFAQ, deleteFAQ } = useFAQ({ loadData: true });
  const modal = useModal();
  const [editingFaq, setEditingFaq] = useState<IFAQ | null>(null);

  const handleAddFAQ = async (data: { question: string; answer: string }) => {
    loader.start();
    try {
      await createFAQ(data);
      alert.toastSuccess("FAQ added successfully");
      modal.close();
    } catch (error) {
      console.error("Error adding FAQ:", error);
      alert.error("Error", "Failed to add FAQ");
    } finally {
      loader.stop();
    }
  };

  const handleEditFAQ = (faq: IFAQ) => {
    setEditingFaq(faq);
    handleShowForm(faq);
  };

  const handleUpdateFAQ = async (data: { question: string; answer: string }, faqId?: string) => {
    const id = faqId || (editingFaq?.id);
    if (!id) return alert.error("Error", "No FAQ selected for update");

    loader.start();
    try {
      await updateFAQ(id, data);
      alert.toastSuccess("FAQ updated successfully");
      setEditingFaq(null);
      modal.close();
    } catch (error) {
      console.error("Error updating FAQ:", error);
      alert.error("Error", "Failed to update FAQ");
    } finally {
      loader.stop();
    }
  };

  const handleDeleteFAQ = async (id: string) => {
    if (!await alert.confirm("Are you sure you want to delete this FAQ?")) return;
    loader.start();
    try {
      await deleteFAQ(id);
      alert.toastSuccess("FAQ deleted successfully");
    } catch (error) {
      console.error("Error deleting FAQ:", error);
      alert.error("Error", "Failed to delete FAQ");
    } finally {
      loader.stop();
    }
  };

  const handleShowForm = (faq?: IFAQ) => {
    modal.open({
      title: faq ? "Edit FAQ" : "Add New FAQ",
      content: <FAQForm
        onSubmit={faq
          ? (data) => handleUpdateFAQ(data, faq.id)
          : handleAddFAQ
        }
        onCancel={() => {
          setEditingFaq(null);
          modal.close();
        }}
        defaultValues={
          faq
            ? { question: faq.question, answer: faq.answer }
            : undefined
        }
        isEditing={!!faq}
      />
    });
  };

  return (
    <div className={`nc-AccountFAQ ${className}`} data-nc-id="AccountFAQ">
      <CommonLayout>
        <div className="space-y-6 sm:space-y-8">
          <div className="flex justify-between items-center">
            <h2 className="text-3xl font-semibold">Frequently Asked Questions</h2>
            <ButtonPrimary onClick={() => handleShowForm()}>Add New FAQ</ButtonPrimary>
          </div>
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

          <div className="space-y-4">
            {faqs.length > 0 ? (
              orderBy(faqs, ["createdAt"], "desc").map((faq) => (
                <FAQItem
                  key={faq.id}
                  faq={faq}
                  onEdit={() => handleEditFAQ(faq)}
                  onDelete={(id) => handleDeleteFAQ(id)}
                />
              ))
            ) : (
              <p className="text-neutral-500 dark:text-neutral-400">
                No FAQs found. Click "Add New FAQ" to create one.
              </p>
            )}
          </div>
        </div>
      </CommonLayout>
    </div>
  );
};

export default AccountFAQ;
